import {Linking, ScrollView, StyleSheet, View} from 'react-native';
import {DataTable, List, Subheading} from 'react-native-paper';

import {
  getCreditUsageByPropertyURL,
  getLimitedPartnerCreditUsageURL,
  useCreditBalanceReport,
} from '../hooks/useFunctions';
import {useState} from 'react';
import {useReservationYears} from '../hooks/useDomainData';

async function handleDownloadCreditUsageByPropertyReport(year: string) {
  const reportURL = await getCreditUsageByPropertyURL(year);
  await Linking.openURL(reportURL);
}

async function handleDownloadLimitedPartnerReport(year: string) {
  const reportURL = await getLimitedPartnerCreditUsageURL(year);
  await Linking.openURL(reportURL);
}

export const CreditBalanceReport = (props: {year: string}) => {
  const creditBalancesReport = useCreditBalanceReport(props.year);
  const allCreditsByFund: {[keyof: string]: number} = {};
  const allUsedCreditsByFund: {[keyof: string]: number} = {};
  if (creditBalancesReport) {
    for (const fund of Object.keys(creditBalancesReport)) {
      // initialize for this fund if not initialized yet
      if (!allCreditsByFund[fund]) {
        allCreditsByFund[fund] = 0;
      }
      if (!allUsedCreditsByFund[fund]) {
        allUsedCreditsByFund[fund] = 0;
      }
      for (const lineItem of Object.keys(creditBalancesReport[fund])) {
        if (lineItem.endsWith(' - Used')) {
          allUsedCreditsByFund[fund] += creditBalancesReport[fund][lineItem];
        } else {
          allCreditsByFund[fund] += creditBalancesReport[fund][lineItem];
        }
      }
    }
  }

  return (
    <>
      {!creditBalancesReport && (
        <DataTable key={props.year}>
          <DataTable.Header>
            <DataTable.Title>
              {props.year} Credit Balances Report Loading...
            </DataTable.Title>
          </DataTable.Header>
        </DataTable>
      )}
      {creditBalancesReport &&
        Object.keys(creditBalancesReport).map(fund => (
          <View key={fund + props.year} style={styles.content}>
            <Subheading>
              {fund} {props.year} Credit Balances Report
            </Subheading>
            <DataTable key={fund}>
              <DataTable.Header>
                <DataTable.Title>Credit Type</DataTable.Title>
                <DataTable.Title>Sum</DataTable.Title>
              </DataTable.Header>
              <DataTable.Row>
                <DataTable.Cell>Sum of all available credits</DataTable.Cell>
                <DataTable.Cell>{allCreditsByFund[fund]}</DataTable.Cell>
              </DataTable.Row>
              <DataTable.Row>
                <DataTable.Cell>Sum of all used credits</DataTable.Cell>
                <DataTable.Cell>{allUsedCreditsByFund[fund]}</DataTable.Cell>
              </DataTable.Row>
              <DataTable.Row>
                <DataTable.Cell>Available credits remaining</DataTable.Cell>
                <DataTable.Cell>
                  {allCreditsByFund[fund] + allUsedCreditsByFund[fund]}
                </DataTable.Cell>
              </DataTable.Row>
              <DataTable.Row>
                <List.Accordion title={'Details'}>
                  {Object.keys(creditBalancesReport[fund])
                    .sort((a, b) => a.localeCompare(b))
                    .map(balance => (
                      <List.Item
                        key={'balance' + balance}
                        title={balance}
                        description={creditBalancesReport[fund][balance]}
                      />
                    ))}
                </List.Accordion>
              </DataTable.Row>
            </DataTable>
          </View>
        ))}
    </>
  );
};

export const Reports = () => {
  const reservationYears = useReservationYears();
  const [balancesExpanded, setBalancesExpanded] = useState(false);
  const handleBalancesPress = () => setBalancesExpanded(!balancesExpanded);

  return (
    <ScrollView style={[styles.container, styles.content]}>
      <List.Accordion
        title={'Credit Usage By Property Report'}
        left={props => <List.Icon {...props} icon="folder" />}>
        {reservationYears &&
          reservationYears.length > 0 &&
          reservationYears.map((year, index) => (
            <List.Item
              // eslint-disable-next-line react-native/no-inline-styles
              style={{paddingLeft: 50}}
              key={index}
              title={'Download for reservation year ' + year.name}
              left={props => (
                <List.Icon {...props} icon="file-document-multiple" />
              )}
              onPress={() =>
                handleDownloadCreditUsageByPropertyReport(year.name)
              }
            />
          ))}
      </List.Accordion>
      <List.Accordion
        title={'Credit Usage By Limited Partners Report'}
        left={props => <List.Icon {...props} icon="file-document-multiple" />}>
        {reservationYears &&
          reservationYears.length > 0 &&
          reservationYears.map((year, index) => (
            <List.Item
              // eslint-disable-next-line react-native/no-inline-styles
              style={{paddingLeft: 50}}
              key={index}
              title={'Download for reservation year ' + year.name}
              left={props => (
                <List.Icon {...props} icon="file-document-multiple" />
              )}
              onPress={() => handleDownloadLimitedPartnerReport(year.name)}
            />
          ))}
      </List.Accordion>
      {reservationYears && reservationYears.length > 0 && (
        <List.Accordion
          title={'Credit Balances By Funds Report ' + reservationYears[0].name}
          left={props => <List.Icon {...props} icon="file-document-multiple" />}
          expanded={balancesExpanded}
          // expanded={true}
          onPress={() => handleBalancesPress()}>
          <CreditBalanceReport year={reservationYears[0].name} />
        </List.Accordion>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    position: 'relative',
  },
  content: {
    padding: 10,
  },
  center: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
});
